import React, { useEffect } from 'react';
import { styled } from '@linaria/react';
import { css } from '@linaria/core';
import useTranslation from 'next-translate/useTranslation';
import { useAtom } from 'jotai';
import Button from '../../components/Button';
import { fontSizeMap } from '../../components/Button';
import { Text, Skeleton, breakpoints, colors } from '@spaceshipapp/cambridge';
import { Service } from '../../types/common';
import { getService, getArrivalDate, isShipmentTypeAvailable, isShipmentDomestic } from './utils';
import Currency from '../../utils/currency';
import Formatter from '../../utils/formatter';
import {
  typeAtom,
  originCountryAtom,
  serviceAtom,
  destinationCountryAtom,
} from '../../contexts/shipment';
import quotationAtom from '../../contexts/quotation';
import { getTrans } from '../../utils/helpers';

interface Props {
  loading?: boolean;
}

const SelectedService: React.FC<Props> = ({ loading }) => {
  const { t, lang } = useTranslation('common');
  const [quotation] = useAtom(quotationAtom);
  const [service] = useAtom(serviceAtom);
  const selectedService = getService(service, quotation);

  const renderDeliveryDays = (service: Service) => {
    if (!service.maxDeliveryDays) return null;

    const date = Formatter.date(
      getArrivalDate(service.maxDeliveryDays),
      t(`Datetime.dateFormatWithWeek`),
      lang,
    );
    return t('ServiceSelection.arrivesOnDate', { date });
  };

  return (
    <LeftPad>
      <Skeleton width="16rem" height="1.25rem" isLoaded={!loading}>
        <StyledText>
          <span>
            {selectedService
              ? Currency.format(selectedService.totalRate)
              : t('ServiceSelection.pricePlaceholder')}{' '}
          </span>{' '}
          <span>{t('ServiceSelection.taxReminder')}</span>
          <span>
            {selectedService
              ? renderDeliveryDays(selectedService)
              : t('ServiceSelection.arrivesOnPlaceholder')}
          </span>
        </StyledText>
      </Skeleton>
      <Skeleton className={skeletonStyle} isLoaded={!loading}>
        <Subtext>
          {selectedService
            ? t('ServiceSelection.serviceSupportedBy', {
                carrierName: getTrans(selectedService.carrierName, lang) as string,
              })
            : t('ServiceSelection.serviceSupportedByPlaceholder')}
        </Subtext>
      </Skeleton>
    </LeftPad>
  );
};

interface FooterProps {
  loading?: boolean;
  redirecting?: boolean;
  isSliderOpen: boolean;
  toggleSlider(): void;
  proceedBooking(): void;
}

const Footer: React.FC<FooterProps> = ({
  loading,
  redirecting,
  isSliderOpen,
  toggleSlider,
  proceedBooking,
}) => {
  const { t } = useTranslation('common');
  const [service] = useAtom(serviceAtom);
  const [originCountry] = useAtom(originCountryAtom);
  const [receiverCountry] = useAtom(destinationCountryAtom);
  const [shipmentType] = useAtom(typeAtom);

  const isAvailable = isShipmentTypeAvailable(originCountry, shipmentType);
  const isDomestic = isShipmentDomestic(originCountry, receiverCountry);

  return (
    <Container>
      <StyledButton
        variant="primary"
        onClick={proceedBooking}
        disabled={loading || !isAvailable || isDomestic}
        loading={redirecting}
        data-tracking-label={t('commonEn:ServiceSelection.buttonSubmit')}
      >
        {t('ServiceSelection.buttonSubmit')}
      </StyledButton>
      <LeftAlignButton
        variant="link"
        iconName={isAvailable ? (isSliderOpen ? `ArrowDown` : `ArrowUp`) : undefined}
        aria-label={
          isSliderOpen ? t('ServiceSelection.buttonCollapse') : t('ServiceSelection.buttonExpand')
        }
        data-tracking-label={
          isSliderOpen
            ? t('commonEn:ServiceSelection.buttonCollapse')
            : t('commonEn:ServiceSelection.buttonExpand')
        }
        size="sm"
        onClick={toggleSlider}
        disabled={loading || !isAvailable || !service || isDomestic}
      >
        {isAvailable && !isDomestic && <SelectedService loading={loading} />}
        {!isAvailable && <Text>{t('ServiceSelection.documentServiceNotAvailable')}</Text>}
        {isDomestic && <Text>{t('ServiceSelection.domesticServiceNotAvailable')}</Text>}
      </LeftAlignButton>
    </Container>
  );
};

const LeftAlignButton = styled(Button)`
  text-align: left;
  text-decoration: none;
  justify-content: flex-start;
  max-width: 34em;
`;

const LeftPad = styled.div`
  margin-left: 1em;
  display: flex;
  flex-direction: column;

  > *:not(:first-child) {
    margin-top: 0.1em;
  }
`;

const StyledButton = styled(Button)`
  font-size: ${fontSizeMap.sm};

  @media (min-width: ${breakpoints.md}) {
    font-size: ${fontSizeMap.md};
  }
`;

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column-reverse;
  align-items: stretch;
  padding-bottom: var(--safe-area-inset-bottom);

  ${StyledButton} {
    /* TODO: Safari bug - does not work correctly for column-reverse */
    /* https://bugs.webkit.org/show_bug.cgi?id=225278 */
    margin-top: 0.5em;
  }

  @media (min-width: ${breakpoints.md}) {
    flex-direction: row-reverse;
    justify-content: space-between;
    align-items: center;

    > *:not(:first-child) {
      margin-right: 1em;
    }

    ${StyledButton} {
      margin-top: 0em;
    }
  }
`;

const StyledText = styled(Text)`
  > span {
    display: inline-block;
    font-size: ${fontSizeMap.sm};
  }
  > span:nth-child(2) {
    color: ${colors.subtext};
  }

  @media (min-width: ${breakpoints.sm}) {
    > span {
      font-size: ${fontSizeMap.md};
      display: inline-block;
    }

    > span:nth-child(3) {
      display: block;
    }
  }

  @media (min-width: ${breakpoints.md}) {
    font-size: ${fontSizeMap.md};
  }
`;

const Subtext = styled(Text)`
  color: ${colors.subtext};
  font-size: ${fontSizeMap.xs};
  @media (min-width: ${breakpoints.md}) {
    font-size: ${fontSizeMap.sm};
  }
`;

const skeletonStyle = css`
  margin-top: 0.25rem;
`;

export default Footer;
